import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
    ArrowRight,
    MapPin,
    Bell,
    CheckCircle2,
    Zap,
    Sparkles,
    Wallet,
    ShieldCheck,
    Clock,
    UserPlus,
    Search,
    Handshake,
    Utensils,
    PartyPopper,
    Sparkle,
    Truck,
    Hotel,
    Building2,
    ChevronDown,
    Star,
} from "lucide-react";

// ---- Data ----
const CATEGORIES = [
    { icon: Utensils, label: "Restauration", desc: "Serveur · Barman · Commis · Plongeur" },
    { icon: PartyPopper, label: "Événementiel", desc: "Hôte / Hôtesse · Animateur · Logistique" },
    { icon: Sparkle, label: "Nettoyage", desc: "Ménage · Entretien · ASH · Propreté" },
    { icon: Truck, label: "Logistique", desc: "Manutention · Livraison · Chef d'équipe" },
    { icon: Hotel, label: "Accueil", desc: "Hôte / Hôtesse · Groom · Réception" },
];

const STEPS = [
    {
        n: "1",
        icon: UserPlus,
        title: "Créez votre profil",
        body: "Métier, expérience, zone géographique et disponibilités. En 5 minutes, votre profil est prêt à recevoir des propositions.",
    },
    {
        n: "2",
        icon: Bell,
        title: "Recevez les opportunités",
        body: "Nous vous envoyons uniquement les missions qui correspondent à votre profil, votre zone et vos disponibilités. Rien de plus.",
    },
    {
        n: "3",
        icon: Handshake,
        title: "Choisissez librement",
        body: "Vous décidez : acceptez celles qui vous intéressent, refusez sans justification les autres. Aucun engagement, aucune pression.",
    },
];

const BENEFITS = [
    {
        icon: Wallet,
        title: "Vous êtes payé directement",
        body: "Le client paie via Renforteo, vous facturez, vous recevez. Aucun intermédiaire d'intérim, aucune commission cachée.",
    },
    {
        icon: MapPin,
        title: "Uniquement des missions près de chez vous",
        body: "Notre moteur géo n'affiche que les propositions dans votre rayon de mobilité. Fini de faire 30 km pour une mission de 3 h.",
    },
    {
        icon: ShieldCheck,
        title: "Contrats & factures automatiques",
        body: "Signature électronique en 1 clic. Facture générée automatiquement à la fin de chaque mission. Compta simplifiée.",
    },
    {
        icon: Clock,
        title: "Missions ponctuelles ou régulières",
        body: "Un soir, un week-end, ou toute la saison — vous choisissez votre rythme. Idéal en complément d'une autre activité.",
    },
];

const TESTIMONIALS = [
    {
        name: "Exemple de situation",
        role: "Serveuse auto-entrepreneuse · Lyon (cas type)",
        stars: 5,
        text: "En 6 mois, plus d'une trentaine de missions réalisées via la plateforme. Choix des soirées, facturation le lendemain, paiement sous 7 jours après validation. Beaucoup plus souple qu'une agence d'intérim.",
    },
    {
        name: "Exemple de situation",
        role: "Manutentionnaire auto-entrepreneur · Grenoble (cas type)",
        stars: 5,
        text: "Horaires respectés, paiement dans les temps. Missions de logistique événementielle en station le week-end, semaines libres. Souplesse impossible en agence d'intérim classique.",
    },
    {
        name: "Exemple de situation",
        role: "Hôtesse d'accueil auto-entrepreneuse · Valence (cas type)",
        stars: 5,
        text: "Le badge « Super Talent » débloque les missions premium (salons, événements corporate haut de gamme). Tarif horaire attractif et clients qualifiés.",
    },
];

const FAQ = [
    {
        q: "Combien ça coûte de s'inscrire ?",
        a: "L'inscription est 100 % gratuite. Renforteo prélève 8 % de frais uniquement sur les missions que vous réalisez. Aucun frais d'abonnement, aucun frais de dossier.",
    },
    {
        q: "Faut-il être auto-entrepreneur ?",
        a: "Oui. Renforteo travaille exclusivement avec des professionnels immatriculés (SIRET à 14 chiffres). Pas encore de SIRET ? On vous accompagne gratuitement — création en 10 min sur le site officiel du gouvernement.",
    },
    {
        q: "Combien de temps pour recevoir ma première mission ?",
        a: "Dès la validation de votre profil (24 à 48 h ouvrées après téléversement de vos justificatifs), vous recevez immédiatement les missions ouvertes dans votre rayon. Les premières candidatures se font en 2 clics.",
    },
    {
        q: "Puis-je refuser une mission ?",
        a: "Bien sûr. Vous êtes indépendant : aucune obligation d'accepter, aucune justification à donner. Nous vous proposons — vous décidez.",
    },
    {
        q: "Quels documents dois-je fournir ?",
        a: "Carte d'identité (recto + verso), justificatif de domicile de moins de 3 mois, RIB, KBIS ou fiche INSEE. Selon le métier : permis (livraison / livreur-monteur), diplôme HACCP (cuisine).",
    },
    {
        q: "Comment suis-je payé ?",
        a: "Chaque mission génère une facture que vous adressez au client via Renforteo. Paiement Stripe sécurisé, virement sous 7 jours après validation du timesheet par le client.",
    },
];

const TRUST = [
    { value: "24 h", label: "délai moyen de matching" },
    { value: "8 %", label: "frais uniques sur missions réalisées" },
    { value: "0 €", label: "frais d'inscription ou d'abonnement" },
    { value: "7 j", label: "délai de paiement après validation" },
];

// ---- SEO ----
function useSEO() {
    useEffect(() => {
        document.title = "Trouvez des missions près de chez vous — Renforteo Pro";
        const setMeta = (name, content, attr = "name") => {
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        const desc =
            "Restauration, événementiel, nettoyage, logistique, accueil : recevez uniquement les missions qui correspondent à votre profil. Inscription gratuite, paiement sous 7 j, aucun engagement.";
        setMeta("description", desc);
        setMeta("og:title", "Renforteo Pro — Trouvez des missions près de chez vous");
        setMeta("og:description", desc);
        setMeta("og:type", "website");
        setMeta("og:url", "https://renforteo.fr/professionnels");
        setMeta("twitter:card", "summary_large_image");

        // JSON-LD
        const jsonld = {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "JobPosting",
                    title: "Missions ponctuelles et régulières pour auto-entrepreneurs",
                    hiringOrganization: {
                        "@type": "Organization",
                        name: "Renforteo",
                        url: "https://renforteo.fr",
                    },
                    employmentType: "CONTRACTOR",
                    industry: "Hospitality, Security, Events, Logistics, Care",
                    jobLocation: {
                        "@type": "Place",
                        address: {
                            "@type": "PostalAddress",
                            addressCountry: "FR",
                        },
                    },
                    description: desc,
                },
                {
                    "@type": "FAQPage",
                    mainEntity: FAQ.map((f) => ({
                        "@type": "Question",
                        name: f.q,
                        acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                },
            ],
        };
        let ld = document.getElementById("pro-jsonld");
        if (!ld) {
            ld = document.createElement("script");
            ld.type = "application/ld+json";
            ld.id = "pro-jsonld";
            document.head.appendChild(ld);
        }
        ld.textContent = JSON.stringify(jsonld);

        let canon = document.querySelector("link[rel=canonical]");
        if (!canon) {
            canon = document.createElement("link");
            canon.setAttribute("rel", "canonical");
            document.head.appendChild(canon);
        }
        canon.setAttribute("href", "https://renforteo.fr/professionnels");
    }, []);
}

// ---- Component ----
export default function ProfessionnelsPage() {
    useSEO();

    return (
        <div data-testid="professionnels-page">
            <Header />

            {/* HERO — focus conversion */}
            <section
                className="relative overflow-hidden bg-gradient-to-br from-[#0B192C] via-[#132845] to-[#0B192C] text-white"
                data-testid="pro-hero"
            >
                <div
                    className="absolute inset-0 opacity-25 pointer-events-none"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 15% 15%, rgba(245,166,35,0.45) 0%, transparent 40%), radial-gradient(circle at 85% 85%, rgba(245,166,35,0.25) 0%, transparent 40%)",
                    }}
                />
                <div className="max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-14 md:pt-24 md:pb-20 relative text-center">
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#F5A623]/40 bg-[#F5A623]/10 px-4 py-1.5 mb-8">
                        <Sparkles size={14} className="text-[#F5A623]" />
                        <span className="text-xs uppercase tracking-widest font-semibold text-[#F5A623]">
                            Espace Professionnels
                        </span>
                    </div>

                    <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight mb-6 max-w-4xl mx-auto">
                        Trouvez de nouvelles missions
                        <br />
                        <span className="text-[#F5A623]">près de chez vous.</span>
                    </h1>

                    <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-10 max-w-3xl mx-auto" data-testid="pro-hero-categories">
                        {CATEGORIES.map((c) => (
                            <span
                                key={c.label}
                                className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-xs md:text-sm font-semibold text-white/90 backdrop-blur"
                            >
                                <c.icon size={13} className="text-[#F5A623]" />
                                {c.label}
                            </span>
                        ))}
                    </div>

                    <div className="flex flex-col items-center gap-3">
                        <div className="text-xs uppercase tracking-widest text-white/60">
                            Inscription gratuite · 2 minutes · sans engagement
                        </div>
                        <Link
                            to="/candidat/inscription"
                            className="btn-gold text-base md:text-lg px-10 py-4"
                            data-testid="pro-hero-signup-btn"
                        >
                            Je crée mon profil
                            <ArrowRight size={18} className="inline ml-2 -mt-0.5" />
                        </Link>
                        <div className="text-xs text-white/50 mt-2">
                            Déjà inscrit ?{" "}
                            <Link
                                to="/connexion"
                                className="text-white/80 hover:text-[#F5A623] underline underline-offset-4 font-semibold"
                                data-testid="pro-hero-login-link"
                            >
                                Je me connecte
                            </Link>
                        </div>
                    </div>

                    {/* Trust strip */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-14 pt-10 border-t border-white/10 max-w-4xl mx-auto">
                        {TRUST.map((m, i) => (
                            <div key={i} data-testid={`pro-trust-${i}`}>
                                <div className="font-display text-2xl md:text-3xl font-black text-[#F5A623]">
                                    {m.value}
                                </div>
                                <div className="text-[11px] md:text-xs text-white/70 mt-1 leading-tight">
                                    {m.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS — 3 étapes claires */}
            <section
                className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24"
                data-testid="pro-steps"
            >
                <div className="text-center mb-14">
                    <div className="overline mb-3">Comment ça marche</div>
                    <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                        Trois étapes. C'est tout.
                    </h2>
                    <p className="text-slate-500 max-w-xl mx-auto mt-4 text-sm md:text-base">
                        Aucune paperasse, aucune agence. Vous restez libre à chaque étape.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-5">
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <div
                                key={i}
                                className="sd-card p-6 md:p-7 flex flex-col relative"
                                data-testid={`pro-step-${i}`}
                            >
                                <div className="absolute -top-4 left-6 font-display text-5xl font-black text-[#F5A623] leading-none">
                                    {s.n}
                                </div>
                                <div className="w-11 h-11 rounded-xl bg-[#0B192C] text-[#F5A623] flex items-center justify-center mb-4 mt-2 ml-auto">
                                    <Icon size={20} />
                                </div>
                                <h3 className="font-display text-xl font-bold text-[#0B192C] mb-3 leading-tight">
                                    {s.title}
                                </h3>
                                <p className="text-sm text-slate-500 leading-relaxed">
                                    {s.body}
                                </p>
                            </div>
                        );
                    })}
                </div>

                <div className="text-center mt-12">
                    <Link
                        to="/candidat/inscription"
                        className="btn-gold text-sm md:text-base"
                        data-testid="pro-steps-cta"
                    >
                        Créer mon profil gratuitement
                        <ArrowRight size={16} className="inline ml-2 -mt-0.5" />
                    </Link>
                </div>
            </section>

            {/* CATEGORIES DETAILS */}
            <section
                className="bg-slate-50 border-y border-slate-200"
                data-testid="pro-categories"
            >
                <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="text-center mb-12">
                        <div className="overline mb-3">Métiers proposés</div>
                        <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                            Trouvez la mission qui vous correspond.
                        </h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        {CATEGORIES.map((c, i) => {
                            const Icon = c.icon;
                            return (
                                <div
                                    key={i}
                                    className="bg-white rounded-xl p-5 border border-slate-200 hover:border-[#F5A623] transition-colors"
                                    data-testid={`pro-cat-${i}`}
                                >
                                    <div className="w-10 h-10 rounded-lg bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center mb-4">
                                        <Icon size={20} />
                                    </div>
                                    <div className="font-display text-base font-bold text-[#0B192C] mb-1">
                                        {c.label}
                                    </div>
                                    <p className="text-xs text-slate-500 leading-relaxed">
                                        {c.desc}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* BENEFITS */}
            <section
                className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24"
                data-testid="pro-benefits"
            >
                <div className="grid md:grid-cols-[1fr_1.5fr] gap-12 items-start">
                    <div>
                        <div className="overline mb-3">Pourquoi Renforteo ?</div>
                        <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C] leading-tight">
                            Une vraie liberté professionnelle.
                        </h2>
                        <p className="text-slate-500 mt-5 leading-relaxed">
                            Vous décidez quand, où, et avec qui vous travaillez. Nous nous occupons
                            uniquement de vous mettre en relation — et de sécuriser le paiement.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {BENEFITS.map((b, i) => {
                            const Icon = b.icon;
                            return (
                                <div
                                    key={i}
                                    className="sd-card p-5"
                                    data-testid={`pro-benefit-${i}`}
                                >
                                    <div className="w-10 h-10 rounded-lg bg-[#0B192C] text-[#F5A623] flex items-center justify-center mb-3">
                                        <Icon size={18} />
                                    </div>
                                    <div className="font-display text-base font-bold text-[#0B192C] mb-2 leading-tight">
                                        {b.title}
                                    </div>
                                    <p className="text-xs text-slate-500 leading-relaxed">
                                        {b.body}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section
                className="bg-slate-50 border-y border-slate-200"
                data-testid="pro-testimonials"
            >
                <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="text-center mb-12">
                        <div className="overline mb-3">Ils l'utilisent</div>
                        <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                            Des pros comme vous.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        {TESTIMONIALS.map((t, i) => (
                            <blockquote
                                key={i}
                                className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col"
                                data-testid={`pro-testimonial-${i}`}
                            >
                                <div className="flex gap-0.5 mb-3">
                                    {Array.from({ length: t.stars }).map((_, k) => (
                                        <Star key={k} size={14} className="fill-[#F5A623] text-[#F5A623]" />
                                    ))}
                                </div>
                                <p className="text-sm text-slate-700 leading-relaxed mb-5 flex-1">
                                    « {t.text} »
                                </p>
                                <div className="pt-4 border-t border-slate-100">
                                    <div className="font-semibold text-sm text-[#0B192C]">
                                        {t.name}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        {t.role}
                                    </div>
                                </div>
                            </blockquote>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24"
                data-testid="pro-faq"
            >
                <div className="text-center mb-12">
                    <div className="overline mb-3">FAQ Pro</div>
                    <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                        Vos questions.
                    </h2>
                </div>
                <div className="space-y-3">
                    {FAQ.map((f, i) => (
                        <details
                            key={i}
                            className="sd-card p-5 md:p-6 group"
                            data-testid={`pro-faq-item-${i}`}
                        >
                            <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                                <span className="font-display text-base md:text-lg text-[#0B192C]">
                                    {f.q}
                                </span>
                                <ChevronDown
                                    size={20}
                                    className="shrink-0 text-slate-400 mt-1 transition-transform group-open:rotate-180"
                                />
                            </summary>
                            <p className="text-sm text-slate-500 leading-relaxed mt-3">
                                {f.a}
                            </p>
                        </details>
                    ))}
                </div>
            </section>

            {/* FINAL CTA */}
            <section
                className="bg-gradient-to-br from-[#F5A623] to-[#f7b74a] text-[#0B192C]"
                data-testid="pro-final-cta"
            >
                <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-20 text-center">
                    <div className="inline-flex items-center gap-2 rounded-full bg-[#0B192C] text-white px-4 py-1.5 mb-6">
                        <Zap size={14} className="text-[#F5A623]" />
                        <span className="text-xs uppercase tracking-widest font-semibold">
                            2 minutes suffisent
                        </span>
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight mb-6">
                        Prêt à recevoir vos premières missions&nbsp;?
                    </h2>
                    <p className="text-sm md:text-base text-[#0B192C]/80 max-w-xl mx-auto mb-10">
                        Inscription 100 % gratuite. Aucune carte bancaire demandée.
                        Vous restez libre d'accepter ou refuser chaque proposition.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <Link
                            to="/candidat/inscription"
                            className="inline-flex items-center gap-2 bg-[#0B192C] text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-[#132845] transition-colors"
                            data-testid="pro-final-cta-signup"
                        >
                            <CheckCircle2 size={16} />
                            Je crée mon profil
                        </Link>
                        <Link
                            to="/assistance"
                            className="inline-flex items-center gap-2 border-2 border-[#0B192C] text-[#0B192C] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-[#0B192C] hover:text-white transition-colors"
                            data-testid="pro-final-cta-help"
                        >
                            Une question ? On vous rappelle
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
